import {defineStore} from "pinia";
import {computed, Ref, ref} from "vue";
import {AxiosResponse, AxiosStatic} from "axios";
import {app} from "@/main";
import {catalogoApi} from "@/requests/catalogo";
import {IBasePaginateResponse} from "@/interfaces/response/IBasePaginateResponse";
import {ICatalogoIstanze} from "@/interfaces/response/catalogoIstanze/ICatalogoIstanze";
import {ICatalogoIstanzeList} from "@/interfaces/response/catalogoIstanze/ICatalogoIstanzeList";
import {IDestinatariList} from "@/interfaces/response/destinatari/IDestinatariList";
import {IDestinatari} from "@/interfaces/response/destinatari/IDestinatari";
import {IArgomentiList} from "@/interfaces/response/argomenti/IArgomentiList";
import {IArgomenti} from "@/interfaces/response/argomenti/IArgomenti";
import {ITipologieList} from "@/interfaces/response/tipologie/ITipologieList";
import {ITipologie} from "@/interfaces/response/tipologie/ITipologie";
import {IService, IServiceFilterIn, IServiceFilterOut} from "cdp-fe-commons-layout";
import {IMaterieList} from "@/interfaces/response/materie/IMaterieList";
import {IMaterie} from "@/interfaces/response/materie/IMaterie";
import { useServiceDetailStore } from "@/stores/service/serviceDetail";



export const useServiceStore = defineStore('service', () => {

    //State
    const services = ref<IService[]>([])
    const servicesCount = ref<number>(0)
    const serviceFilters = ref<IServiceFilterIn>();
    const axios: AxiosStatic = app.config.globalProperties.$axios

    //getters
    const servicesData = computed<Ref<IService[]>>(() => services);
    const servicesDataCount = computed<Ref<number>>(() => servicesCount);
    const serviceFiltersData = computed<Ref<IServiceFilterIn|undefined>>(() => serviceFilters);

    const serviceDetailStore = useServiceDetailStore();

    async function getServiceTitle(codiceCatalogo: string): Promise<string> {
        return axios.get(catalogoApi.GET_TITOLO_CATALOGO_ISTANZE_BY_CODICE_CATALOGO(codiceCatalogo))
            .then((el: AxiosResponse<{ titolo?: string; titoloEn?: string }>) => {
                return serviceDetailStore.language === 'en' && el.data.titoloEn
                    ? el.data.titoloEn
                    : el.data.titolo ?? codiceCatalogo;
            })
            .catch(() => codiceCatalogo);
    }

    //Actions
    async function requestServices(page: number, limit: number, filters?: IServiceFilterOut){
        axios.get<IBasePaginateResponse<ICatalogoIstanzeList>>(catalogoApi.GET_ALL_CATALOGO_ISTANZE(page, limit, filters))
            .then((el: AxiosResponse<IBasePaginateResponse<ICatalogoIstanzeList>>) => {
            servicesCount.value = el.data.page.totalElements;
            services.value = el.data._embedded.catalogoIstanze.map((el: ICatalogoIstanze) => {
                return {
                    id: el.catalogoId,
                    title: el.titolo,
                    titleEn:el.titoloEn,
                    tags: el.tags,
                    tagsEn:el.tagsEn,
                    firmaDigitale:el.firmaDigitale,
                    description: el.descrizioneBreve,
                    descriptionEn:el.descrizioneBreveEn,
                    dataInizioPresentazione: el.presentazioneInizio,
                    dataFinePresentazione: el.presentazioneFine,
                    pubblicato: el.pubblicato,
                    inviabile: el.inviabile,
                    argomento: el.descrizioneArgomento,
                    argomentoRoute: '/argomento/' + el.argomentiId, // Adjust path or ID as needed
                  //  routeLink:'/catalogo_pi/'+el.codiceCdp,
                    routeLink:'/catalogo/'+el.codiceCdp,
                    svgMaterie: el.svgMateria
                } as IService;
            });
        });
    }

    function getArgomentiByMateria(materiaId: string|undefined){
        let url = catalogoApi.GET_ALL_ARGOMENTI();
        if(materiaId !== undefined && materiaId !== ''){
            url = catalogoApi.GET_ALL_ARGOMENTI_BY_MATERIA_ID(materiaId);
        }
        axios.get<IBaseResponse<IArgomentiList>>(url)
            .then(el => {
                const argomenti = el.data._embedded.argomenti.map((p: IArgomenti) => {
                    return {value: p.argomentiId, label: `${p.titoloMateria} - ${p.descrizione}`}
                });
                if(serviceFilters.value){
                    serviceFilters.value.argomenti = argomenti;
                }
            })
    }
    function requestServiceFilters() {
        const endpoints = [
            axios.get(catalogoApi.GET_ALL_ARGOMENTI()),
            axios.get(catalogoApi.GET_ALL_DESTINATARI()),
            axios.get(catalogoApi.GET_ALL_TIPOLOGIE()),
            axios.get(catalogoApi.GET_ALL_MATERIE()),
        ];

        Promise.all(endpoints).then((response) => {
            const argomentiResponse = response[0]?.data as IBaseResponse<IArgomentiList>;
            const destinatariResponse = response[1]?.data as IBaseResponse<IDestinatariList>;
            const tipologieResponse = response[2]?.data as IBaseResponse<ITipologieList>;
            const materieResponse = response[3]?.data as IBaseResponse<IMaterieList>;

            const argomenti = argomentiResponse._embedded.argomenti.map((p: IArgomenti) => {
                return {value: p.argomentiId, label: `${p.titoloMateria} - ${p.descrizione}`}
            });
            const destinatari = destinatariResponse._embedded.destinatari.map((p: IDestinatari) => {
                    return {value: p.destinatariId, label: p.descrizione , labelEn:p.descrizioneEn}
            });
            const tipologie = tipologieResponse._embedded.tipologie.map((p: ITipologie) => {
                return {value: p.tipologieId, label: p.descrizione}
            });
            const materie = materieResponse._embedded.materie.map((p: IMaterie) => {
                return {value: p.materieId, label: p.descrizione}
            });

            serviceFilters.value = {
                destinatari: destinatari,
                argomenti: argomenti,
                tipologie: tipologie,
                materie: materie,
                strutture: []
            } as IServiceFilterIn;

        });
    }


    return {
        //Getters
        servicesData, servicesDataCount, serviceFiltersData,
        //Actions
        requestServices, requestServiceFilters, getServiceTitle, getArgomentiByMateria
    }
})
